import React, { useEffect, useState } from 'react';
import { Phone, Users, Video, ChevronRight, CalendarClock, XCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Interview } from '../../types/hr.types';
import { mockInterviews } from '../../utils/mockData';
import { isToday, isThisWeek, formatTime, formatDate } from '../../utils/dateUtils';
import { ShowMoreModal } from '../ui/ShowMoreModal';
import { CancelRescheduleModal } from '../CancelRescheduleModal';
import { useToast } from '../ui/Toast';
const MAX_VISIBLE = 4;
interface InterviewsWidgetProps {
  type: 'phone' | 'in-person';
  period: 'today' | 'week';
}
export function InterviewsWidget({
  type,
  period
}: InterviewsWidgetProps) {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [actionType, setActionType] = useState<'cancel' | 'reschedule' | null>(null);
  const {
    addToast
  } = useToast();
  useEffect(() => {
    const timer = setTimeout(() => {
      const filtered = mockInterviews.filter(i => {
        const typeMatch = type === 'phone' ? i.type === 'Phone Screen' || i.type === 'Video Call' : i.type === 'In-Person';
        const periodMatch = period === 'today' ? isToday(i.dateTime) : isThisWeek(i.dateTime);
        return typeMatch && periodMatch;
      });
      filtered.sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
      setInterviews(filtered);
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [type, period]);
  const handleAction = (interview: Interview, action: 'cancel' | 'reschedule') => {
    setSelectedInterview(interview);
    setActionType(action);
  };
  const handleConfirmAction = ({
    date,
    note
  }: {
    date?: string;
    note: string;
  }) => {
    if (!selectedInterview || !actionType) return;
    if (actionType === 'cancel') {
      setInterviews(prev => prev.filter(i => i.id !== selectedInterview.id));
      addToast({
        title: 'Interview Canceled',
        message: `Canceled interview with ${selectedInterview.candidateName}. Note: ${note}`,
        type: 'success'
      });
    } else if (actionType === 'reschedule' && date) {
      const updatedInterview = {
        ...selectedInterview,
        dateTime: date
      };
      const periodMatch = period === 'today' ? isToday(date) : isThisWeek(date);
      if (periodMatch) {
        setInterviews(prev => {
          const filtered = prev.filter(i => i.id !== selectedInterview.id);
          return [...filtered, updatedInterview].sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
        });
      } else {
        setInterviews(prev => prev.filter(i => i.id !== selectedInterview.id));
      }
      addToast({
        title: 'Interview Rescheduled',
        message: `Rescheduled interview with ${selectedInterview.candidateName} to ${formatDate(date)} ${formatTime(date)}.`,
        type: 'success'
      });
    }
    setSelectedInterview(null);
    setActionType(null);
  };
  const title = `${type === 'phone' ? 'Phone Screens' : 'In-Person Interviews'} ${period === 'today' ? 'Today' : 'This Week'}`;
  const Icon = type === 'phone' ? Phone : Users;
  const iconColor = type === 'phone' ? 'text-blue-500' : 'text-indigo-500';
  const visibleInterviews = interviews.slice(0, MAX_VISIBLE);
  const hasMore = interviews.length > MAX_VISIBLE;
  const remainingCount = interviews.length - MAX_VISIBLE;
  const InterviewItem = ({
    interview
  }: {
    interview: Interview;
  }) => <div className="group/item relative flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100" data-id="element-1560">
      <div className={`mt-1 p-1.5 rounded-md ${interview.type === 'Video Call' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`} data-id="element-1561">
        {interview.type === 'Video Call' ? <Video className="h-3.5 w-3.5" data-id="element-1562" /> : <Icon className="h-3.5 w-3.5" data-id="element-1563" />}
      </div>
      <div className="flex-1 min-w-0 pr-16" data-id="element-1564">
        <div className="flex justify-between items-start" data-id="element-1565">
          <p className="text-sm font-medium text-gray-900" data-id="element-1566">
            {interview.candidateName}
          </p>
          <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600" data-id="element-1567">
            {period === 'today' ? formatTime(interview.dateTime) : formatDate(interview.dateTime, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
          </span>
        </div>
        <p className="text-xs text-gray-500 truncate" data-id="element-1568">{interview.role}</p>
        <p className="text-xs text-gray-400 mt-0.5" data-id="element-1569">
          w/ {interview.interviewer}
        </p>
      </div>
      <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover/item:opacity-100 transition-opacity bg-white/80 backdrop-blur-sm rounded-md p-0.5 shadow-sm border border-gray-100" data-id="element-1570">
        <button onClick={e => {
        e.stopPropagation();
        handleAction(interview, 'reschedule');
      }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Reschedule" data-id="element-1571">
          <CalendarClock className="h-3.5 w-3.5" data-id="element-1572" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        handleAction(interview, 'cancel');
      }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Cancel" data-id="element-1573">
          <XCircle className="h-3.5 w-3.5" data-id="element-1574" />
        </button>
      </div>
    </div>;
  if (isLoading) {
    return <Card title={title} icon={<Icon className={`h-5 w-5 ${iconColor}`} data-id="element-1576" />} data-id="element-1575">
        <div className="space-y-3 animate-pulse" data-id="element-1577">
          {[1, 2].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg" data-id="element-1578" />)}
        </div>
      </Card>;
  }
  return <>
      <Card title={title} icon={<Icon className={`h-5 w-5 ${iconColor}`} data-id="element-1580" />} data-id="element-1579">
        {interviews.length > 0 ? <div className="space-y-2" data-id="element-1581">
            {visibleInterviews.map(interview => <InterviewItem key={interview.id} interview={interview} data-id="element-1582" />)}
            {hasMore && <button onClick={() => setShowModal(true)} className="w-full mt-2 py-2 px-3 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors flex items-center justify-center gap-1" data-id="element-1583">
                Show {remainingCount} more
                <ChevronRight className="h-4 w-4" data-id="element-1584" />
              </button>}
          </div> : <div className="text-center py-8 text-gray-500" data-id="element-1585">
            <p className="text-sm" data-id="element-1586">All clear! No interviews scheduled.</p>
          </div>}
      </Card>
      <ShowMoreModal isOpen={showModal} onClose={() => setShowModal(false)} title={`All ${title} (${interviews.length})`} data-id="element-1587">
        <div className="space-y-2" data-id="element-1588">
          {interviews.map(interview => <InterviewItem key={interview.id} interview={interview} data-id="element-1589" />)}
        </div>
      </ShowMoreModal>
      <CancelRescheduleModal isOpen={!!selectedInterview} onClose={() => {
      setSelectedInterview(null);
      setActionType(null);
    }} action={actionType} itemType="interview" itemName={selectedInterview?.candidateName || ''} currentDate={selectedInterview?.dateTime} onConfirm={handleConfirmAction} data-id="element-1590" />
    </>;
}